import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import PasswordStrengthMeter, { scorePassword } from "@/components/PasswordStrengthMeter";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [state, setState] = useState({ status: "verifying", email: "", name: "" });
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState({ status: "error", error: "Missing token." }); return; }
    api.get(`/auth/verify-reset-token`, { params: { token } })
      .then((r) => setState({ status: "ready", email: r.data.email, name: r.data.name }))
      .catch((e) => setState({ status: "error",
                                error: toErrMsg(e, "Invalid or expired reset link.") }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (scorePassword(pwd) < 2) { toast.error("Password too weak. Mix upper + lower + digit + symbol."); return; }
    if (pwd !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Password reset successfully — please sign in.");
      setTimeout(() => nav("/login"), 800);
    } catch (err) {
      toast.error(toErrMsg(err, "Failed to reset password"));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-lg border bg-white p-8 shadow-sm" data-testid="reset-password-card">
        <div className="mb-6 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          <h2 className="text-xl font-semibold">Reset your password</h2>
        </div>

        {state.status === "verifying" && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Verifying link…
          </div>
        )}

        {state.status === "error" && (
          <div className="space-y-4">
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700" data-testid="reset-error">{state.error}</div>
            <Link to="/login" className="text-sm text-emerald-600 hover:underline">Back to sign in</Link>
          </div>
        )}

        {state.status === "ready" && (
          <form onSubmit={submit} className="space-y-4">
            <div className="rounded-md border bg-slate-50 p-2 text-[12px] text-slate-700">
              Setting a new password for <strong>{state.email}</strong>
              {state.name ? <> · {state.name}</> : null}
            </div>
            <div>
              <Label htmlFor="pwd">New password</Label>
              <Input id="pwd" type="password" value={pwd}
                     onChange={(e) => setPwd(e.target.value)} required minLength={8}
                     data-testid="reset-new-password" autoComplete="new-password" />
              <PasswordStrengthMeter password={pwd} testid="reset-pwd-strength" />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input id="confirm" type="password" value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} required minLength={8}
                     data-testid="reset-confirm-password" autoComplete="new-password" />
            </div>
            <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800"
                    disabled={busy} data-testid="reset-submit">
              {busy ? "Saving…" : "Reset password"}
            </Button>
            <div className="text-center text-sm">
              <Link to="/login" className="text-emerald-600 hover:underline">Back to sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
